<template>
  <div class="space-y-6 p-12 font-intervariable max-h-[80vh] min-w-[80vw] overflow-hidden flex flex-col">
    

    <!-- Tabs Container -->
    <div 
      ref="tabsContainer"
      class="sticky top-0 z-10 bg-muted backdrop-blur-sm border-b mb-0 rounded-full mx-5 mb-4 flex-shrink-0"
    >
      <div class="flex gap-2 p-1.5 w-full overflow-x-hidden rounded-full justify-evenly">
        <button
          v-for="section in sections" 
          :key="section.id"
          @click.stop="activeSection = section.id"
          class="relative pl-2 pr-1.5 py-1.5 text-sm font-medium transition-all duration-300 rounded-full min-w-[120px] text-center"
          :class="[
            activeSection === section.id
              ? 'text-background bg-primary shadow-sm' 
              : 'text-muted-foreground hover:text-foreground/80'
          ]"
        >
          <div class="flex items-center justify-between">
            <p class="truncate overflow-hidden h-5">{{ section.label }}</p>
            <Badge 
              v-if="section.score"
              :variant="activeSection === section.id ? 'secondary' : 'outline'"
              class="text-[0.8em] py-0 px-1 ml-1 border-none ring-0"
            >
              {{ section.score }}
            </Badge>
          </div>
        </button>
      </div>
    </div>

    <!-- Tab Content -->
    <div class="flex-1 overflow-hidden">
      <ScrollArea class="h-full w-full">
        <div class="space-y-6 px-5">
          <!-- Usage Analytics Tab -->
          <section v-show="activeSection === 'analytics'" class="space-y-6">
            <!-- Usage Analytics Chart -->
            <Card class="bg-background border-0 shadow-none">
              <CardContent>
                <ClientOnly>
                  <div ref="chartContainer" class="w-full h-[300px] p-0 m-0">
                    <VChart 
                      v-if="chartOptions && isClientMounted" 
                      :option="chartOptions" 
                      :autoresize="true"
                      @chart:ready="onChartReady"
                      style="width: 100%; height: 100%;"
                    />
                    <div v-else class="flex items-center justify-center w-full h-full">
                      <Icon name="eos-icons:loading" class="w-6 h-6 text-primary animate-spin" />
                    </div>
                  </div>
                  <template #fallback>
                    <div class="w-full h-[300px] flex items-center justify-center bg-muted/10 rounded-lg border-none">
                      <Icon name="eos-icons:loading" class="w-6 h-6 text-primary animate-spin" />
                    </div>
                  </template>
                </ClientOnly>
              </CardContent>
            </Card>

            <!-- Usage Summary -->
            <div v-if="usageByService.length > 0">
              <h3 class="font-semibold mb-3">Usage by Service</h3>
              <div class="space-y-2">
                  <div 
                    v-for="service in usageByService" 
                    :key="service.type"
                    class="flex justify-between items-center p-3 border- rounded-lg bg-card"
                  >
                    <div class="flex-1">
                      <div class="flex items-center gap-2 mb-1">
                        <Icon :name="getServiceIcon(service.type)" class="w-4 h-4 text-muted-foreground" />
                        <p class="font-medium text-sm">{{ service.displayName }}</p>
                      </div>
                      <p class="text-xs text-muted-foreground">
                        {{ service.totalQuantity.toLocaleString() }} {{ service.unit }}
                        {{ service.avgUnitPrice ? `· ${formatCurrency(service.avgUnitPrice)} each` : '' }}
                      </p>
                    </div>
                    <div class="text-right">
                      <p class="font-medium text-sm text-red-600">-{{ formatCurrency(service.totalCost) }}</p>
                      <p class="text-xs text-muted-foreground">{{ service.transactions }} transactions</p>
                    </div>
                  </div>
                </div>
            </div>

          
          </section>

          <!-- Recent Transactions Tab -->
          <section v-show="activeSection === 'transactions'" class="space-y-6">
            <!-- Recent Transactions -->
            <div v-if="recentTransactions.length > 0">
              <h3 class="font-semibold mb-3">Recent Transactions</h3>
              <ScrollArea class="max-h-[400px]" thumbClass="bg-muted-foreground/30 hover:bg-muted-foreground/50">
                <div class="space-y-2 pr-4">
                  <div 
                    v-for="tx in recentTransactions" 
                    :key="tx.id"
                    class="flex justify-between items-center p-3 text-sm border rounded-lg bg-card hover:bg-muted/50 transition-colors"
                  >
                    <div class="flex-1">
                      <div class="flex items-center gap-2 mb-1">
                        <Icon :name="getTransactionIcon(tx)" class="w-4 h-4 text-muted-foreground" />
                        <p class="font-medium">{{ parseTransactionTitle(tx.description) }}</p>
                      </div>
                      <p class="text-xs text-muted-foreground">
                        {{ new Date(tx.created * 1000).toLocaleDateString() }}
                        {{ parseTransactionDetails(tx.description) }}
                      </p>
                    </div>
                    <div class="text-right">
                      <div :class="tx.amount < 0 ? 'text-green-600' : 'text-red-600'" class="font-medium">
                        {{ tx.amount < 0 ? '+' : '-' }}{{ formatCurrency(Math.abs(tx.amount)) }}
                      </div>
                      <p class="text-xs text-muted-foreground">
                        {{ getTransactionType(tx) }}
                      </p>
                    </div>
                  </div>
                </div>
              </ScrollArea>
            </div>

            <!-- Empty State for Transactions -->
            <div v-if="!loading && recentTransactions.length === 0" class="text-center py-8">
              <Icon name="fluent:receipt-28-filled" class="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              
              <p class="text-muted-foreground mb-4">Your credit purchases and usage will appear here</p>
            </div>
          </section>

          <!-- Spending Limit Tab -->
          <section v-show="activeSection === 'limits'" class="space-y-6">
            <!-- Spending Limit Control -->
            <div class="space-y-4">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <Label for="spending-limit">Monthly Spending Limit</Label>
                  <Icon 
                    v-if="spendingLimitLoading" 
                    name="eos-icons:loading" 
                    class="w-4 h-4 text-primary animate-spin" 
                  />
                </div>
                <span class="text-sm font-medium text-muted-foreground">
                  ${{ spendingLimit.toFixed(2) }}
                </span>
              </div>
              
              <!-- Spending Limit Buttons and Input -->
              <div class="space-y-4">
                <h3 class="text-sm text-muted-foreground select-none transition-all ml-1" :class="{'opacity-0 translate-x-10': showLimitSetButton}">Set Spending Limit</h3>
                
                <div class="flex items-start gap-3 relative">
                  <!-- Quick Limit Buttons -->
                  <Button
                    v-for="limit in spendingLimitOptions" 
                    :key="limit"
                    @click="setSpendingLimitPreset(limit)"
                    :disabled="spendingLimitLoading"
                    class="h-10 px-3 squircle-15 rounded-lg bg-white dark:bg-background text-primary hover:bg-foreground/90 hover:text-background dark:hover:bg-primary text-sm font-medium flex-shrink-0 select-none transition-all z-10 min-w-12"
                    :class="{
                      '!opacity-0 translate-x-10 pointer-events-none': showLimitSetButton,
                      'bg-primary text-background': spendingLimit === limit
                    }"
                  >
                    <span class="text-sm p-0">
                      <div class="absolute top-0 -left-3 p-0 w-full h-full flex items-center justify-center pointer-events-none">
                        <Icon v-if="spendingLimitLoading && clickedLimitPreset === limit" name="lucide:loader-2" class="w-4 h-4 animate-spin" />
                      </div>
                      <span class="opacity-100" :class="{'!opacity-0': spendingLimitLoading && clickedLimitPreset === limit}">
                        ${{ limit }}
                      </span>
                    </span>
                  </Button>
                  
                  <!-- Set Limit Area Container -->
                  <div ref="limitAreaRef" class="absolute right-0 flex items-center gap-2">
                    <!-- Set Button -->
                    <Button 
                      @click="setCustomSpendingLimit" 
                      :disabled="spendingLimitLoading"
                      class="h-10 px-3 rounded-xl bg-primary dark:bg-primary dark:text-background text-background hover:bg-black hover:text-background dark:hover:bg-white dark:hover:text-background text-sm font-medium flex-shrink-0 select-none transition-all"
                      :class="{'!opacity-0 translate-x-10': !showLimitSetButton}"
                    >
                      <span v-if="!spendingLimitLoading">
                        Set
                      </span>
                      <span v-else>
                        <Icon name="lucide:loader-2" class="w-4 h-4 animate-spin" />
                      </span>
                    </Button>
                    
                    <!-- Custom Amount Input -->
                    <NumberField 
                      v-model="customSpendingLimit"
                      :min="0"
                      :max="1000"
                      :step="10"
                      :format-options="{
                        style: 'currency',
                        currency: 'AUD',
                        currencyDisplay: 'symbol'
                      }"
                      class="w-20 min-w-24"
                      @keydown.enter="setCustomSpendingLimit"
                    >
                      <NumberFieldContent class="h-10">
                        <NumberFieldDecrement />
                        <NumberFieldInput 
                          placeholder="$100"
                          class="text-center text-xs bg-white dark:bg-background !rounded-xl"
                        />
                        <NumberFieldIncrement />
                      </NumberFieldContent>
                    </NumberField>
                  </div>
                </div>
              </div>
              
              <!-- Error display -->
              <div v-if="spendingLimitError" class="p-3 bg-red-50 border border-red-200 rounded-lg">
                <div class="flex items-center gap-2">
                  <Icon name="lucide:alert-circle" class="w-4 h-4 text-red-600" />
                  <p class="text-sm text-red-700">{{ spendingLimitError }}</p>
                </div>
                <Button 
                  @click="loadSpendingLimit" 
                  variant="outline" 
                  size="sm" 
                  class="mt-2"
                >
                  Retry
                </Button>
              </div>
              
              <div class="text-sm text-muted-foreground">
                {{ spendingLimit > 0 
                  ? `You will receive warnings when approaching $${spendingLimit.toFixed(2)} in monthly usage. This helps you monitor and control your credit spending.` 
                  : 'No spending limit set - you can monitor usage without automatic warnings.' 
                }}
              </div>
            </div>

            <!-- Balance Details (if multiple balances exist) -->
            <div v-if="creditsData?.balance_details && creditsData.balance_details.length > 1" class="space-y-2">
              <h3 class="text-sm font-medium text-muted-foreground">Balance Breakdown</h3>
              <div class="grid gap-2">
                <div 
                  v-for="(balance, index) in creditsData.balance_details" 
                  :key="index"
                  class="flex justify-between items-center p-3 bg-muted/30 rounded-lg text-sm"
                >
                  <span class="text-muted-foreground">Balance {{ index + 1 }}</span>
                  <div class="text-right">
                    <div class="font-medium">{{ formatCurrency(balance.available_balance) }}</div>
                    <div class="text-xs text-muted-foreground">
                      Ledger: {{ formatCurrency(balance.ledger_balance) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </ScrollArea>
    </div>

    <!-- Error Display -->
    <div v-if="error" class="p-3 bg-red-50 border border-red-200 rounded-lg flex-shrink-0">
      <p class="text-sm text-red-700">{{ error }}</p>
      <Button @click="fetchCredits" variant="outline" size="sm" class="mt-2">
        Retry
      </Button>
    </div>
  </div>
</template>

<script setup>
import { use } from 'echarts/core'
import { LineChart, BarChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, LegendComponent, TitleComponent } from 'echarts/components'
import { ScrollArea } from '@/components/ui/scroll-area'
import { NumberField } from '@/components/ui/number-field'
import { NumberFieldContent } from '@/components/ui/number-field'
import { NumberFieldInput } from '@/components/ui/number-field'
import { NumberFieldDecrement } from '@/components/ui/number-field'
import { NumberFieldIncrement } from '@/components/ui/number-field'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'

// Only import and use ECharts on client-side
if (import.meta.client) {
  use([LineChart, BarChart, GridComponent, TooltipComponent, LegendComponent, TitleComponent])
}

useSquircleObserver()

const { 
  credits,
  formatCredits, 
  hasCredits,
  recentTransactions, 
  loading,
  error,
  fetchCredits,
  purchaseCredits,
  consumeCredits,
  creditsData
} = useCredits()

// Tab state
const activeSection = ref('analytics')
const tabsContainer = ref(null)

// Tab sections
const sections = computed(() => [
  { 
    id: 'analytics', 
    label: "Usage Analytics",
    score: usageByService.value.length ? `${usageByService.value.length}` : null
  },
  { 
    id: 'transactions', 
    label: "Recent Transactions",
    score: recentTransactions.value.length ? `${recentTransactions.value.length}` : null
  },
  { 
    id: 'limits', 
    label: "Spending Limit",
    score: spendingLimit.value > 0 ? `$${spendingLimit.value}` : 'None'
  }
])

// Spending limit state
const currentSpendingLimit = ref(0)
const spendingLimit = computed(() => currentSpendingLimit.value)
const spendingLimitLoading = ref(false)
const spendingLimitError = ref('')

// Spending limit options (preset buttons)
const spendingLimitOptions = [50, 100, 200, 500]

// Custom input state
const customSpendingLimit = ref(null)
const showLimitSetButton = ref(false)
const clickedLimitPreset = ref(0)
const limitAreaRef = ref()

// Load spending limit from Supabase/API
const loadSpendingLimit = async () => {
  try {
    spendingLimitLoading.value = true
    spendingLimitError.value = ''
    
    const response = await $fetch('/api/billing/spending-limit', {
      method: 'GET'
    })
    
    if (response.success) {
      currentSpendingLimit.value = response.spending_limit || 0
      console.log('✅ Loaded spending limit:', {
        spending_limit: response.spending_limit,
        source: response.source
      })
    }
  } catch (error) {
    console.error('❌ Failed to load spending limit:', error)
    spendingLimitError.value = 'Failed to load spending limit'
  } finally {
    spendingLimitLoading.value = false
  }
}

// Set spending limit preset
const setSpendingLimitPreset = async (limit) => {
  if (spendingLimitLoading.value) return
  
  clickedLimitPreset.value = limit
  try {
    await updateSpendingLimit(limit)
    currentSpendingLimit.value = limit
  } finally {
    clickedLimitPreset.value = 0
  }
}

// Set custom spending limit
const setCustomSpendingLimit = async () => {
  if (!customSpendingLimit.value || customSpendingLimit.value < 0 || spendingLimitLoading.value) return
  
  try {
    await updateSpendingLimit(customSpendingLimit.value)
    currentSpendingLimit.value = customSpendingLimit.value
    customSpendingLimit.value = null
    showLimitSetButton.value = false
  } catch (error) {
    // Error handling is done in updateSpendingLimit
  }
}

// Update spending limit in Supabase/API
const updateSpendingLimit = async (newLimit) => {
  try {
    spendingLimitLoading.value = true
    spendingLimitError.value = ''
    
    const response = await $fetch('/api/billing/spending-limit', {
      method: 'POST',
      body: {
        spending_limit: newLimit
      }
    })
    
    if (response.success) {
      console.log('✅ Updated spending limit:', {
        spending_limit: response.spending_limit,
        source: response.source,
        stripe_sync: response.stripe_sync_attempted
      })
    }
  } catch (error) {
    console.error('❌ Failed to update spending limit:', error)
    spendingLimitError.value = error.data?.message || 'Failed to update spending limit'
    
    // Revert the UI change if API call failed
    await loadSpendingLimit()
  } finally {
    spendingLimitLoading.value = false
  }
}

// Chart state
const chartContainer = ref(null)
const isClientMounted = ref(false)

// Chart data processing
const usageChartData = computed(() => {
  if (!recentTransactions.value || recentTransactions.value.length === 0) {
    return { dates: [], spending: [], cumulative: [] }
  }

  // Sort transactions by date (oldest first)
  const sortedTransactions = [...recentTransactions.value]
    .filter(tx => tx.amount > 0) // Only consumption transactions
    .sort((a, b) => a.created - b.created)

  const dates = []
  const spending = []
  const cumulative = []
  let runningTotal = 0

  sortedTransactions.forEach(tx => {
    const date = new Date(tx.created * 1000)
    const amount = tx.amount / 100 // Convert from cents
    
    dates.push(date.toLocaleDateString())
    spending.push(amount)
    runningTotal += amount
    cumulative.push(runningTotal)
  })

  return { dates, spending, cumulative }
})

// Chart options
const chartOptions = computed(() => {
  const { dates, spending, cumulative } = usageChartData.value
  
  // Generate date range for the last month even if no data
  const generateDateRange = () => {
    const endDate = new Date()
    const startDate = new Date()
    startDate.setMonth(startDate.getMonth() - 3) // Go back 3 months for more billing periods
    
    const billingPeriods = []
    const currentDate = new Date(startDate)
    
    // Generate monthly billing periods
    while (currentDate <= endDate) {
      const periodStart = new Date(currentDate)
      const periodEnd = new Date(currentDate)
      periodEnd.setMonth(periodEnd.getMonth() + 1)
      periodEnd.setDate(0) // Last day of the current month
      
      // Format as billing period: "Jun-Jul"
      const startMonth = periodStart.toLocaleDateString('en-GB', { month: 'short' })
      const endMonth = periodEnd.toLocaleDateString('en-GB', { month: 'short' })
      
      // If same month, just show the month, otherwise show range
      const billingPeriod = startMonth === endMonth ? startMonth : `${startMonth}-${endMonth}`
      billingPeriods.push(billingPeriod)
      
      currentDate.setMonth(currentDate.getMonth() + 1) // Move to next month
    }
    
    return billingPeriods
  }
  
  // Use actual data if available, otherwise generate empty data with realistic date range
  const chartDates = dates.length > 0 
    ? dates.map((date, index) => {
        // Convert existing dates to billing periods
        const d = new Date(date)
        const nextMonth = new Date(d)
        nextMonth.setMonth(nextMonth.getMonth() + 1)
        
        const currentMonth = d.toLocaleDateString('en-GB', { month: 'short' })
        const nextMonthStr = nextMonth.toLocaleDateString('en-GB', { month: 'short' })
        
        return `${currentMonth}-${nextMonthStr}`
      })
    : generateDateRange()
  const chartSpending = dates.length > 0 ? spending : new Array(chartDates.length).fill(0)
  const chartCumulative = dates.length > 0 ? cumulative : new Array(chartDates.length).fill(0)
  
  // Create credit grants bars - simulate some grant data for demo
  const creditGrants = dates.length > 0 
    ? new Array(chartDates.length).fill(credits.value / chartDates.length) // Distribute current credits across time
    : new Array(chartDates.length).fill(25) // Demo grant amounts

  return {
    backgroundColor: 'transparent',
    title: {
      text: dates.length > 0 ? '' : '',
      textStyle: {
        fontSize: 16,
        fontWeight: 'normal',
        color: dates.length > 0 ? '#374151' : '#9CA3AF'
      }
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross'
      },
      className: 'echarts-tooltip-custom',
      formatter: (params) => {
        let result = `<div class="tooltip-content">`
        result += `<div class="tooltip-title">${params[0].axisValue}</div>`
        result += `<div class="tooltip-details">`
        params.forEach(param => {
          const value = param.seriesName === 'Daily Spend' 
            ? `$${param.value.toFixed(2)}` 
            : param.seriesName === 'Cumulative'
            ? `$${param.value.toFixed(2)} total`
            : param.seriesName === 'Credit Grants'
            ? `$${param.value.toFixed(2)} granted`
            : `$${param.value.toFixed(2)} balance`
          result += `<div class="tooltip-detail">
            <div class="tooltip-icon" style="background-color: ${param.color}; width: 12px; height: 12px; border-radius: 50%; flex-shrink: 0;"></div>
            <span class="tooltip-label">${param.seriesName}</span>
            <span class="tooltip-value">${value}</span>
          </div>`
        })
        result += `</div></div>`
        return result
      }
    },
    legend: {
      data: ['Daily Spend', 'Cumulative', 'Credit Grants'],
      textStyle: {
        color: dates.length > 0 ? '#374151' : '#9CA3AF'
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      top: '15%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: chartDates,
      axisPointer: {
        type: 'shadow'
      },
      axisLabel: {
        color: dates.length > 0 ? '#374151' : '#9CA3AF'
      },
      axisLine: {
        lineStyle: {
          color: dates.length > 0 ? '#D1D5DB' : '#E5E7EB'
        }
      }
    },
    yAxis: [
      {
        type: 'value',
        position: 'left',
        min: 0,
        max: 100, // Realistic upper limit
        axisLabel: {
          formatter: '${value}',
          color: dates.length > 0 ? '#374151' : '#9CA3AF'
        },
        axisLine: {
          lineStyle: {
            color: dates.length > 0 ? '#D1D5DB' : '#E5E7EB'
          }
        },
        splitLine: {
          lineStyle: {
            color: dates.length > 0 ? '#F3F4F6' : '#F9FAFB',
            type: 'dashed'
          }
        }
      }
    ],
    series: [
      {
        name: 'Daily Spend',
        type: 'bar',
        data: chartSpending,
        itemStyle: {
          color: dates.length > 0 ? '#ec4899' : '#E5E7EB',
          opacity: dates.length > 0 ? 1 : 0.5
        }
      },
      {
        name: 'Credit Grants',
        type: 'bar',
        data: creditGrants,
        itemStyle: {
          color: dates.length > 0 ? '#86efac' : '#C4B5FD',
          opacity: 0.7
        },
        stack: 'credits'
      },
      {
        name: 'Cumulative',
        type: 'line',
        data: chartCumulative,
        smooth: true,
        itemStyle: {
          color: dates.length > 0 ? '#1f2937' : '#D1D5DB'
        },
        lineStyle: {
          width: 3,
          color: dates.length > 0 ? '#1f2937' : '#D1D5DB'
        }
      }
    ]
  }
})

// Chart ref for drag functionality
const chartRef = ref(null)

// Update spending limit graphic when chart is ready or spending limit changes
const updateSpendingLimitGraphic = () => {
  if (!chartRef.value || spendingLimit.value <= 0) {
    return
  }

  nextTick(() => {
    const chart = chartRef.value
    if (!chart) return

    try {
      // Get grid coordinates
      const gridComponent = chart.getModel().getComponent('grid', 0)
      const gridRect = gridComponent.coordinateSystem.getArea()
      
      // Calculate Y position for the spending limit
      const yPixel = chart.convertToPixel('grid', [0, spendingLimit.value])[1]
      
      // Update or create the draggable line graphic
      chart.setOption({
        graphic: [
          {
            type: 'line',
            id: 'spending-limit-line',
            shape: {
              x1: gridRect.x,
              y1: yPixel,
              x2: gridRect.x + gridRect.width,
              y2: yPixel
            },
            style: {
              stroke: '#ef4444',
              lineWidth: 4,
              lineDash: [8, 4],
              opacity: 0.9
            },
            draggable: true,
            cursor: 'ns-resize',
            z: 999,
            ondrag: function() {
              // Convert pixel position back to data value
              const newDataValue = chart.convertFromPixel('grid', [0, this.shape.y1])
              if (newDataValue && newDataValue[1] >= 0 && newDataValue[1] <= 100) {
                // Update spending limit - round to nearest $5
                const newLimit = Math.round(newDataValue[1] / 5) * 5
                currentSpendingLimit.value = Math.max(0, Math.min(100, newLimit))
              }
            },
            onmouseover: function() {
              this.style.stroke = '#dc2626'
              this.style.lineWidth = 5
              this.style.opacity = 1
            },
            onmouseout: function() {
              this.style.stroke = '#ef4444'
              this.style.lineWidth = 4
              this.style.opacity = 0.9
            }
          },
          {
            type: 'text',
            id: 'spending-limit-label',
            style: {
              text: `$${spendingLimit.value} spending limit (drag to adjust)`,
              x: gridRect.x + gridRect.width - 10,
              y: yPixel - 5,
              fill: '#ef4444',
              fontSize: 12,
              fontWeight: 'bold',
              textAlign: 'right'
            },
            z: 998
          }
        ]
      })
    } catch (error) {
      console.warn('Could not update spending limit graphic:', error)
    }
  })
}

// Watch for spending limit changes
watch(spendingLimit, updateSpendingLimitGraphic)

// Update graphic on chart ready
const onChartReady = (chart) => {
  chartRef.value = chart
  updateSpendingLimitGraphic()
}

// Handle window resize  
const handleResize = () => {
  updateSpendingLimitGraphic()
}

// Check if this is an individual customer account
const isIndividualCustomer = computed(() => {
  // This would be determined by the API response
  return true // Since we're now using individual customers
})

// Enhanced usage analytics
const usageByService = computed(() => {
  const serviceMap = new Map()
  
  recentTransactions.value
    .filter(tx => tx.amount > 0) // Positive amounts are usage/consumption
    .forEach(tx => {
      const parsed = parseUsageTransaction(tx)
      if (!parsed) return
      
      const key = parsed.serviceType
      if (!serviceMap.has(key)) {
        serviceMap.set(key, {
          type: parsed.serviceType,
          displayName: parsed.serviceName,
          unit: parsed.unit,
          totalCost: 0,
          totalQuantity: 0,
          transactions: 0,
          unitPrices: []
        })
      }
      
      const service = serviceMap.get(key)
      service.totalCost += tx.amount / 100
      service.totalQuantity += parsed.quantity || 1
      service.transactions++
      if (parsed.unitPrice) service.unitPrices.push(parsed.unitPrice)
    })
  
  return Array.from(serviceMap.values()).map(service => ({
    ...service,
    avgUnitPrice: service.unitPrices.length > 0 
      ? service.unitPrices.reduce((a, b) => a + b, 0) / service.unitPrices.length 
      : null
  })).sort((a, b) => b.totalCost - a.totalCost)
})

// Parse usage transaction details from description
const parseUsageTransaction = (tx) => {
  const desc = tx.description || ''
  
  // Enhanced parsing for different service patterns
  const patterns = [
    // "Credit Purchase - 50 AUD"
    { 
      regex: /^Credit Purchase - ([0-9.]+) ([A-Z]+)/, 
      extract: (match) => ({
        serviceName: 'Credit Purchase',
        serviceType: 'credit_purchase',
        quantity: 1,
        unit: 'purchase',
        totalCost: parseFloat(match[1])
      })
    },
    // "Credit consumption - 5.50"
    {
      regex: /^Credit consumption - ([0-9.]+)/,
      extract: (match) => ({
        serviceName: 'Credit Usage',
        serviceType: 'credit_consumption',
        quantity: 1,
        unit: 'usage',
        totalCost: parseFloat(match[1])
      })
    },
    // "OpenAI GPT-4: 1,250 tokens ($0.03)"
    { 
      regex: /^([^:]+):\s*([0-9,]+)\s*([^(]+)\s*\(\$([0-9.]+)\)/, 
      extract: (match) => ({
        serviceName: match[1],
        serviceType: match[1].toLowerCase().replace(/\s+/g, '_'),
        quantity: parseInt(match[2].replace(/,/g, '')),
        unit: match[3].trim(),
        totalCost: parseFloat(match[4])
      })
    },
    // Generic fallback
    {
      regex: /^(.+)/,
      extract: (match) => ({
        serviceName: match[1],
        serviceType: 'general',
        quantity: 1,
        unit: 'usage'
      })
    }
  ]
  
  for (const pattern of patterns) {
    const match = desc.match(pattern.regex)
    if (match) {
      return pattern.extract(match)
    }
  }
  
  return null
}

const parseTransactionTitle = (description) => {
  const parsed = parseUsageTransaction({ description })
  return parsed?.serviceName || description?.split(':')[0] || 'Transaction'
}

const parseTransactionDetails = (description) => {
  const parsed = parseUsageTransaction({ description })
  if (!parsed) return ''
  
  let details = []
  if (parsed.quantity && parsed.quantity > 1) {
    details.push(`${parsed.quantity.toLocaleString()} ${parsed.unit}`)
  }
  if (parsed.model) {
    details.push(parsed.model)
  }
  if (parsed.unitPrice) {
    details.push(`${formatCurrency(parsed.unitPrice)} each`)
  }
  
  return details.length > 0 ? `· ${details.join(' · ')}` : ''
}

const getServiceIcon = (serviceType) => {
  const icons = {
    'credit_purchase': 'lucide:plus-circle',
    'credit_consumption': 'lucide:minus-circle',
    'openai': 'lucide:brain',
    'image_generation': 'lucide:image',
    'gpt': 'lucide:message-square',
    'dall_e': 'lucide:palette',
    'api_calls': 'lucide:network',
    'general': 'lucide:activity'
  }
  return icons[serviceType] || 'lucide:circle'
}

const getTransactionIcon = (tx) => {
  if (tx.amount < 0) return 'lucide:plus-circle' // Credit added
  return 'lucide:minus-circle' // Credit used
}

const getTransactionType = (tx) => {
  if (tx.amount < 0) return 'Credit Added'
  return 'Credit Used'
}

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4
  }).format(amount)
}

const formatDataSource = (dataSource) => {
  const sources = {
    'credit_balance_summary_api': 'Real-time API',
    'stripe_sync_engine': 'Synced Data',
    'webhook_data': 'Webhook Data',
    'cached': 'Cached'
  }
  return sources[dataSource] || dataSource
}

// Watch for custom spending limit changes
watch(customSpendingLimit, (newVal) => {
  showLimitSetButton.value = !!newVal
})

// Load credits on mount
onMounted(() => {
  isClientMounted.value = true
  fetchCredits()
  loadSpendingLimit() // Load current spending limit from Stripe
  
  // Add resize listener for draggable spending limit graphic
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  // Clean up resize listener
  window.removeEventListener('resize', handleResize)
})
</script>

<style>
.echarts-tooltip-custom {
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(8px) !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 12px !important;
  width: 300px !important;
}

.dark .echarts-tooltip-custom {
  background: rgba(30, 30, 30, 0.8) !important;
}

/* Tooltip Styles */
.tooltip-content {
  font-family: system-ui, -apple-system, sans-serif;
  width: 100%;
}

.tooltip-title {
  font-size: 1.1em;
  font-weight: 600;
  margin-bottom: 8px;
  color: var(--foreground);
  line-height: 1.3;
  hyphens: auto;
  word-wrap: break-word;
}

.tooltip-details {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
  width: 100%;
}

.tooltip-detail {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 8px;
}

.tooltip-icon {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tooltip-label {
  flex: 0 0 auto;
  color: var(--muted-foreground);
  font-size: 0.9em;
  min-width: 80px;
}

.tooltip-value {
  flex: 1;
  color: var(--foreground);
  font-weight: 500;
  text-align: right;
}

.dark .tooltip-title {
  color: rgb(230, 230, 230);
}

.dark .tooltip-label {
  color: rgb(180, 180, 180);
}

.dark .tooltip-value {
  color: rgb(230, 230, 230);
}
</style>